import type { ClassDefinition } from "@hd2/data";
import { useEffect, useState } from "react";

let cache: ClassDefinition[] | null = null;
let pending: Promise<ClassDefinition[]> | null = null;

async function fetchClasses(): Promise<ClassDefinition[]> {
	if (cache) {
		return cache;
	}
	if (!pending) {
		pending = fetch("/api/classes")
			.then((r) => r.json() as Promise<{ classes: ClassDefinition[] }>)
			.then((data) => {
				cache = data.classes;
				return cache;
			});
	}
	return pending;
}

export function useClasses(): {
	classes: ClassDefinition[];
	loading: boolean;
} {
	const [classes, setClasses] = useState<ClassDefinition[]>(() => cache ?? []);
	const [loading, setLoading] = useState(cache === null);

	useEffect(() => {
		if (cache) {
			// Cache was populated between useState init and this effect (race
			// across mounts/components). Sync local state instead of leaving
			// loading=true forever.
			setClasses(cache);
			setLoading(false);
			return;
		}
		let alive = true;
		fetchClasses()
			.then((list) => {
				if (alive) {
					setClasses(list);
					setLoading(false);
				}
			})
			.catch(() => {
				if (alive) {
					setLoading(false);
				}
			});
		return () => {
			alive = false;
		};
	}, []);

	return { classes, loading };
}
